<template>
  <div v-editable="slide" class="relative size-full bg-gold">
    <div class="absolute inset-0">
      <AssetImage
        v-if="image"
        :blok="image"
        :lazy="index > 0"
        class="!mt-0 h-full"
        cover
      />
    </div>
    <Responsive
      :base-width="isMobile ? 0 : 1920"
      class="!absolute size-full sm:!relative"
      :ratio="ratio"
    >
      <div
        class="relative flex size-full select-none items-center justify-center overflow-hidden text-center"
        :class="[
          alignX === 'left' && 'sm:justify-start sm:text-left',
          alignX === 'center' && 'sm:justify-center sm:text-center',
          alignX === 'right' && 'sm:justify-end sm:text-right',
          alignY === 'top' && 'sm:items-start',
          alignY === 'middle' && 'sm:items-center',
          alignY === 'bottom' && 'sm:items-end',
        ]"
        @click="ctaClicked(slide, index)"
      >
        <div
          class="relative flex w-full flex-col gap-6 p-4 sm:w-1/2 sm:p-16"
          :class="{ 'sm:px-24': hasMultipleSlides }"
        >
          <StageKicker :data="slide" />
          <StageHeadline :data="slide" />
          <div v-if="!slide.hide_button && ctaButton">
            <component
              :is="ctaButton.component"
              :blok="ctaButton"
              @click.stop="ctaClicked(slide, index)"
            />
          </div>
        </div>
      </div>
      <CMSLink
        v-if="bannerWithLink"
        :cms-link="ctaButton?.ctaLink"
        :open-new-tab="ctaButton?.openNewTab"
        :product-id="ctaButton?.product_id"
        :order-number="ctaButton?.order_number"
        :aria-label="ariaLabel"
        class="absolute inset-0 opacity-0"
        @click="ctaClicked(slide, index)"
      />
    </Responsive>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { SbStageSlide } from '~/cms/storyblok/types/storyblok.d.ts'

const { t } = useI18n()

const props = defineProps({
  slide: {
    type: Object as PropType<SbStageSlide>,
    required: true,
  },
  index: {
    type: Number as PropType<number>,
    required: true,
  },
  ratio: {
    type: String as PropType<string>,
    default: undefined,
  },
  hasMultipleSlides: {
    type: Boolean as PropType<boolean>,
    required: true,
  },
})
const { slide } = props
const { isMobile } = useDeviceType()
const alignX = computed(() => slide.align_x || 'center')
const alignY = computed(() => slide.align_y || 'middle')
const { ctaClicked } = inject('stage')! as any // emit does not work here

const ctaButton = computed(() => props.slide.cta_button?.[0])
const image = computed(() => props.slide.image?.[0])

const bannerWithLink = computed(
  () =>
    ctaButton.value?.order_number ||
    ctaButton.value?.product_id ||
    ctaButton.value?.ctaLink,
)

const ariaLabel = computed(() => {
  const label = image.value?.altDescription
  if (!label) {
    return ''
  }
  const isTargetBlank = ctaButton.value?.openNewTab
  return isTargetBlank ? getTargetBlankLabel(label, t) : label
})

// Tailwind includes:
// text-gold-10
// text-gold-20
// text-gold-40
// text-gold-60
// text-gold-80
// text-gold
// text-lightGold-20
// text-lightGold-40
// text-lightGold-60
// text-lightGold-80
// text-lightGold
// text-black-10
// text-black-20
// text-black-40
// text-black-50
// text-black-60
// text-black-80
// text-black
// text-white-20
// text-white-40
// text-white-60
// text-white-80
// text-white
</script>
